import { useState, useRef, useEffect, useCallback } from "react";
import {
  Tabs,
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Divider,
  Row,
  Col as AntCol,
  Typography,
  DatePicker,
  Upload,
  Badge,
  Alert,
  Modal,
  message,
  Grid,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  UploadOutlined,
  BankOutlined,
  BookOutlined,
  CarOutlined,
  TeamOutlined,
  CalendarOutlined,
  PlusOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";
import dayjs from "dayjs";
import ReportColumnSettings from "../../components/academics/ReportColumnSettings";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

// ─── Shared wrapper for each settings section ───────────────────────────────
const SettingsSection = ({ title, description, children }) => (
  <div className="mb-4">
    {title && (
      <Title level={5} className="mb-1">
        {title}
      </Title>
    )}
    {description && (
      <Text type="secondary" className="d-block mb-3" style={{ fontSize: 13 }}>
        {description}
      </Text>
    )}
    {children}
  </div>
);

const Col = ({ span, xs, md, ...rest }) => {
  const shouldAutoStack = span === 12 || span === 8;
  const resolvedXs = xs ?? (shouldAutoStack ? 24 : undefined);
  const resolvedMd = md ?? (span === 12 ? 12 : span === 8 ? 8 : undefined);

  return <AntCol span={span} xs={resolvedXs} md={resolvedMd} {...rest} />;
};

const ResponsiveTable = (props) => (
  <Table scroll={{ x: "max-content" }} {...props} />
);

const ErrorDeck = ({ messages = [], onDismiss }) => {
  if (!messages.length) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10000,
        width: "min(760px, 92vw)",
      }}
    >
      <Alert
        type="error"
        showIcon
        closable
        onClose={onDismiss}
        message="Term could not be switched"
        description={
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {messages.map((msg, index) => (
              <li key={`${String(msg)}-${index}`}>{String(msg)}</li>
            ))}
          </ul>
        }
        style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.14)" }}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  1. GENERAL / SCHOOL INFO
// ═══════════════════════════════════════════════════════════════════════════════
const GeneralSettings = () => {
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const isLgUp = !!screens.lg;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [hasInformation, setHasInformation] = useState(false);

  const toViewUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("//") ||
      url.startsWith("data:") ||
      url.startsWith("blob:")
    ) {
      return url;
    }
    const backend = String(Settings?.backend || "").replace(/\/+$/, "");
    return backend ? `${backend}${url.startsWith("/") ? "" : "/"}${url}` : url;
  };

  const mapApiToForm = (data = {}) => ({
    school_name: data?.school_name || "",
    motto: data?.motto || "",
    address: data?.address || "",
    po_box: data?.po_box || "",
    phone: data?.phone || "",
    email: data?.email || "",
    website: data?.website || "",
    primary_color: data?.primary_color || "#1F6FEB",
    country: data?.country || "Uganda",
    currency: data?.currency || "UGX",
    timezone: data?.timezone || "Africa/Kampala",
  });

  const getSchoolInformation = async () => {
    setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.bootstrap?.information,
    );
    setLoading(false);

    if (res?.ok && res?.data) {
      setHasInformation(true);
      form.setFieldsValue(mapApiToForm(res.data));
      setLogoUrl(toViewUrl(res.data?.school_logo_url));
      return;
    }

    if (res?.status === 404) {
      setHasInformation(false);
      form.setFieldsValue(mapApiToForm({}));
      setLogoUrl(null);
      return;
    }

    message.error(res?.msg || "Failed to load school information");
  };

  useEffect(() => {
    getSchoolInformation();
  }, []);

  const saveSchoolInformation = async () => {
    const values = await form.validateFields();
    const payload = {
      school_name: values.school_name,
      motto: values.motto || "",
      address: values.address || "",
      po_box: values.po_box || "",
      phone: values.phone || "",
      email: values.email || "",
      website: values.website || "",
      primary_color: values.primary_color || "#1F6FEB",
      country: values.country || "Uganda",
      currency: values.currency || "UGX",
      timezone: values.timezone || "Africa/Kampala",
    };

    setSaving(true);
    const method = hasInformation ? "PATCH" : "POST";
    const url = hasInformation
      ? Settings?.urls?.bootstrap?.informationUpdate
      : Settings?.urls?.bootstrap?.information;
    const res = await Utilities.fetchWithAuth(method, url, payload);
    setSaving(false);

    if (res?.ok) {
      message.success(
        hasInformation
          ? "School information updated successfully"
          : "School information created successfully",
      );
      setHasInformation(true);
      getSchoolInformation();
      return;
    }

    message.error(res?.msg || "Failed to save school information");
  };

  const uploadSchoolLogo = async ({ file }) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("school_logo", file);
    setLogoUploading(true);
    const res = await Utilities.fetchWithAuth(
      "PATCH",
      Settings?.urls?.bootstrap?.informationLogo,
      formData,
    );
    setLogoUploading(false);

    if (res?.ok) {
      const nextLogoUrl = toViewUrl(res?.data?.school_logo_url);
      if (nextLogoUrl) setLogoUrl(nextLogoUrl);
      message.success("School logo updated successfully");
      return;
    }

    message.error(res?.msg || "Failed to upload school logo");
  };

  return (
    <div>
      <Alert
        message="School Information"
        description="These settings define your institution's identity and are used across the system (reports, letters, receipts, etc.)."
        type="info"
        showIcon
        className="mb-4"
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={saveSchoolInformation}
        initialValues={mapApiToForm({})}
      >
        <Row gutter={isLgUp ? 24 : 12}>
          <Col xs={24} lg={12}>
            <SettingsSection title="Institution Details">
              <Form.Item
                label="School Name"
                name="school_name"
                rules={[{ required: true, message: "School name is required" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item label="Motto" name="motto">
                <Input />
              </Form.Item>
              <Form.Item label="Address" name="address">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item label="P.O. Box" name="po_box">
                <Input />
              </Form.Item>
              <Row gutter={isLgUp ? 16 : 12}>
                <Col span={12}>
                  <Form.Item label="Phone" name="phone">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Email" name="email">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Website" name="website">
                <Input />
              </Form.Item>
            </SettingsSection>
          </Col>

          <Col xs={24} lg={12}>
            <SettingsSection title="Branding">
              <Form.Item label="School Logo">
                <Space direction="vertical" style={{ width: "100%" }}>
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="school logo"
                      style={{
                        width: isLgUp ? "auto" : "100%",
                        maxWidth: 220,
                        maxHeight: 120,
                        objectFit: "contain",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        padding: 8,
                        background: "#fff",
                      }}
                    />
                  ) : (
                    <Text type="secondary">No logo uploaded</Text>
                  )}
                  <Upload
                    listType="picture"
                    maxCount={1}
                    showUploadList={false}
                    customRequest={uploadSchoolLogo}
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={logoUploading}
                      style={!isLgUp ? { width: "100%" } : undefined}
                    >
                      Upload Logo
                    </Button>
                  </Upload>
                </Space>
              </Form.Item>
              <Form.Item label="Primary Color" name="primary_color">
                <Input placeholder="#1F6FEB" />
              </Form.Item>
            </SettingsSection>

            <SettingsSection title="Regional">
              <Row gutter={isLgUp ? 16 : 12}>
                <Col span={12}>
                  <Form.Item label="Country" name="country">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Currency" name="currency">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={isLgUp ? 16 : 12}>
                <Col span={12}>
                  <Form.Item label="Timezone" name="timezone">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </SettingsSection>
          </Col>
        </Row>

        <Divider />
        <Space
          direction={isLgUp ? "horizontal" : "vertical"}
          style={{ width: isLgUp ? "auto" : "100%" }}
        >
          <Button
            type="primary"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={saving}
            style={!isLgUp ? { width: "100%" } : undefined}
          >
            Save Changes
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={getSchoolInformation}
            loading={loading}
            style={!isLgUp ? { width: "100%" } : undefined}
          >
            Reset
          </Button>
        </Space>
      </Form>
    </div>
  );
};

// ─── Signature canvas pad ─────────────────────────────────────────────────────
const SignaturePad = ({ canvasRef, width = 440, height = 180 }) => {
  const isDrawing = useRef(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => {
    isDrawing.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          cursor: "crosshair",
          touchAction: "none",
          display: "block",
          background: "#fafafa",
          width: "100%",
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={stopDraw}
        onMouseLeave={stopDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={stopDraw}
      />
      <Button size="small" className="mt-2" onClick={clearCanvas}>
        Clear
      </Button>
    </div>
  );
};

// ─── Report Card / Signature Settings ─────────────────────────────────────────
const SignatureSettings = () => {
  const SIGNATURE_NAME = "Report Card Signature";
  const [signatures, setSignatures] = useState([]);
  const [rolesByUserId, setRolesByUserId] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [currentSig, setCurrentSig] = useState(null);
  const [currentUserID, setCurrentUserID] = useState(null);
  const [uploadedSignatureFile, setUploadedSignatureFile] = useState(null);
  const [uploadedSignaturePreview, setUploadedSignaturePreview] =
    useState(null);
  const canvasRef = useRef(null);

  const parsedSessionUser = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "null");
    } catch (_) {
      return null;
    }
  })();
  const sessionUserId =
    parsedSessionUser?.userID ??
    parsedSessionUser?.userId ??
    parsedSessionUser?.id ??
    null;

  const getSignatureImageUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("//") ||
      url.startsWith("data:") ||
      url.startsWith("blob:")
    ) {
      return url;
    }
    const backend = String(Settings?.backend || "").replace(/\/+$/, "");
    if (!backend) return url;
    return `${backend}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const fetchSignatures = async () => {
    setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.bootstrap?.signature,
    );
    if (res?.ok) {
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setSignatures(list);
      if (sessionUserId != null) {
        const existingMine = list.find(
          (sig) =>
            String(sig?.userID ?? sig?.userId ?? "") === String(sessionUserId),
        );
        setCurrentSig(existingMine || null);
      }
    }
    setLoading(false);
  };

  const fetchStaffRoles = async () => {
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.rbac?.userRoles,
    );
    if (res?.ok) {
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      const roleSets = {};
      list.forEach((item) => {
        if (Number(item?.block) === 1) return;
        const userId = item?.user ?? item?.userID ?? item?.userId;
        const roleName = item?.roleName ?? item?.role?.name ?? null;
        if (userId == null || !roleName) return;
        const key = String(userId);
        if (!roleSets[key]) roleSets[key] = new Set();
        roleSets[key].add(String(roleName));
      });

      const mappedRoles = Object.fromEntries(
        Object.entries(roleSets).map(([userId, roles]) => [
          userId,
          Array.from(roles).join(", "),
        ]),
      );
      setRolesByUserId(mappedRoles);
    }
  };

  useEffect(() => {
    setCurrentUserID(sessionUserId != null ? String(sessionUserId) : null);
    fetchSignatures();
    fetchStaffRoles();
  }, []);

  useEffect(
    () => () => {
      if (uploadedSignaturePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedSignaturePreview);
      }
    },
    [uploadedSignaturePreview],
  );

  const mySignatures =
    currentUserID != null
      ? signatures.filter(
          (sig) =>
            String(sig?.userID ?? sig?.userId ?? "") === String(currentUserID),
        )
      : [];

  const openModal = () => {
    const mine = mySignatures[0] || null;
    setCurrentSig(mine || currentSig || null);
    setUploadedSignatureFile(null);
    setUploadedSignaturePreview(null);
    setModalOpen(true);
    setTimeout(() => {
      if (canvasRef.current) {
        canvasRef.current
          .getContext("2d")
          .clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }, 100);
  };

  const canvasHasInk = (canvas) => {
    if (!canvas) return false;
    const pixel = canvas
      .getContext("2d")
      .getImageData(0, 0, canvas.width, canvas.height).data;
    return pixel.some((v) => v !== 0);
  };

  const submitSignature = async (
    imageBlob = null,
    fileName = "signature.png",
  ) => {
    const formData = new FormData();
    formData.append("name", SIGNATURE_NAME);
    formData.append("userID", String(currentUserID));
    if (imageBlob) formData.append("imageField", imageBlob, fileName);

    const mine = mySignatures[0] || null;
    if (mine?.id) formData.append("id", mine.id);

    setSaving(true);
    const res = await Utilities.fetchWithAuth(
      "POST",
      Settings?.urls?.bootstrap?.signature,
      formData,
    );
    setSaving(false);
    if (res?.ok) {
      message.success("Signature saved successfully");
      setModalOpen(false);
      setUploadedSignatureFile(null);
      setUploadedSignaturePreview(null);
      fetchSignatures();
    } else {
      message.error(res?.msg || "Failed to save signature");
    }
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!currentUserID) {
      message.error("Unable to determine current user. Please sign in again.");
      return;
    }

    if (uploadedSignatureFile) {
      submitSignature(
        uploadedSignatureFile,
        uploadedSignatureFile.name || "signature.png",
      );
      return;
    }

    const hasCanvasSignature = canvasHasInk(canvas);
    if (!hasCanvasSignature && (mySignatures[0]?.id || currentSig?.id)) {
      submitSignature();
      return;
    }

    if (!hasCanvasSignature) {
      message.error(
        "Please draw a signature or upload a signature image before saving",
      );
      return;
    }

    canvas.toBlob(async (blob) => {
      if (!blob) {
        message.error("Please draw a signature");
        return;
      }
      submitSignature(blob, "signature.png");
    }, "image/png");
  };

  const handleSignatureUpload = (file) => {
    if (!file?.type?.startsWith("image/")) {
      message.error("Please upload an image file");
      return Upload.LIST_IGNORE;
    }

    if (uploadedSignaturePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(uploadedSignaturePreview);
    }

    setUploadedSignatureFile(file);
    setUploadedSignaturePreview(URL.createObjectURL(file));
    if (canvasRef.current) {
      canvasRef.current
        .getContext("2d")
        .clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    return false;
  };

  const deleteSignature = (sig) => {
    if (!sig?.id) return;

    Modal.confirm({
      title: "Delete My Signature",
      content: "Are you sure you want to delete your signature?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setDeletingId(sig.id);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.bootstrap?.signature}/${sig.id}`,
        );
        setDeletingId(null);

        if (res?.ok) {
          message.success("Signature deleted successfully");
          setCurrentSig(null);
          setModalOpen(false);
          fetchSignatures();
        } else {
          message.error(res?.msg || "Failed to delete signature");
        }
      },
    });
  };

  const columns = [
    { title: "Staff Name", dataIndex: "fullName" },
    {
      title: "Role",
      dataIndex: "userRoleName",
      render: (v, r) => {
        const userId = r?.userID ?? r?.userId ?? r?.user?.id;
        const resolvedRole =
          v || (userId != null ? rolesByUserId[String(userId)] : null);
        return resolvedRole || <Text type="secondary">—</Text>;
      },
    },
    {
      title: "Signature Preview",
      dataIndex: "imageUrl",
      render: (v, r) => {
        const previewUrl = getSignatureImageUrl(v || r?.imageField);
        return previewUrl ? (
          <img
            src={previewUrl}
            alt="signature"
            style={{ height: 40, maxWidth: 160, objectFit: "contain" }}
          />
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "",
      width: 120,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" type="link" onClick={() => openModal()}>
            <i className="fas fa-edit" />
          </Button>
          <Button
            size="small"
            type="link"
            danger
            loading={deletingId === r?.id}
            onClick={() => deleteSignature(r)}
          >
            <i className="fas fa-trash" />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <SettingsSection
      title="Report Card Signatures"
      description="Manage your digital e-signature for report cards. Each user can only maintain their own signature."
    >
      <div className="mb-2">
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          {currentSig?.id ? "Edit My Signature" : "Add My Signature"}
        </Button>
      </div>
      <ResponsiveTable
        dataSource={mySignatures}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        bordered
        loading={loading}
      />
      <Modal
        title={currentSig ? "Edit My Signature" : "Add My Signature"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={saveSignature}
        okText="Save Signature"
        confirmLoading={saving}
        width={520}
        destroyOnClose
      >
        <label className="fw-bold d-block mb-1">
          Signature Image <span style={{ color: "red" }}>*</span>
        </label>
        <Text
          type="secondary"
          style={{ fontSize: 12, display: "block", marginBottom: 8 }}
        >
          Sign in the area below using your mouse or finger, or upload a
          signature image.
        </Text>
        <SignaturePad canvasRef={canvasRef} />
        <Divider plain style={{ margin: "16px 0 12px" }}>
          or
        </Divider>
        <Upload
          accept="image/*"
          maxCount={1}
          beforeUpload={handleSignatureUpload}
          onRemove={() => {
            if (uploadedSignaturePreview?.startsWith("blob:")) {
              URL.revokeObjectURL(uploadedSignaturePreview);
            }
            setUploadedSignatureFile(null);
            setUploadedSignaturePreview(null);
          }}
          fileList={
            uploadedSignatureFile
              ? [
                  {
                    uid:
                      uploadedSignatureFile.uid || uploadedSignatureFile.name,
                    name: uploadedSignatureFile.name,
                    status: "done",
                  },
                ]
              : []
          }
        >
          <Button icon={<UploadOutlined />}>Upload Signature Image</Button>
        </Upload>
        {uploadedSignaturePreview && (
          <div className="mt-3">
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 6 }}
            >
              Uploaded preview
            </Text>
            <img
              src={uploadedSignaturePreview}
              alt="uploaded signature preview"
              style={{
                height: 72,
                maxWidth: "100%",
                objectFit: "contain",
                border: "1px solid #f0f0f0",
                borderRadius: 4,
                padding: 6,
              }}
            />
          </div>
        )}
      </Modal>
    </SettingsSection>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  2. ACADEMIC SETTINGS
// ═══════════════════════════════════════════════════════════════════════════════
const AcademicSettings = () => {
  const screens = useBreakpoint();
  const isLgUp = !!screens.lg;
  const [gradingSystems, setGradingSystems] = useState([]);
  const [selectedGradingSystemId, setSelectedGradingSystemId] = useState(null);
  const [grades, setGrades] = useState([]);
  const [attendanceRemarks, setAttendanceRemarks] = useState([]);
  const [learningAreas, setLearningAreas] = useState([]);
  const [preschoolGrades, setPreschoolGrades] = useState([]);
  const [preschoolCompetences, setPreschoolCompetences] = useState([]);
  const [preschoolClasses, setPreschoolClasses] = useState([]);
  const [preschoolTerms, setPreschoolTerms] = useState([]);

  const [gradingLoading, setGradingLoading] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [preschoolLoading, setPreschoolLoading] = useState(false);
  const [competenceLoading, setCompetenceLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [editingAttendanceRemark, setEditingAttendanceRemark] = useState(null);
  const [learningAreaModalOpen, setLearningAreaModalOpen] = useState(false);
  const [editingLearningArea, setEditingLearningArea] = useState(null);
  const [preschoolGradeModalOpen, setPreschoolGradeModalOpen] = useState(false);
  const [editingPreschoolGrade, setEditingPreschoolGrade] = useState(null);
  const [competenceModalOpen, setCompetenceModalOpen] = useState(false);
  const [editingCompetence, setEditingCompetence] = useState(null);
  const [competenceClassFilter, setCompetenceClassFilter] = useState(null);
  const [competenceTermFilter, setCompetenceTermFilter] = useState(null);
  const [competenceColumnFilter, setCompetenceColumnFilter] = useState(null);

  const [gradeForm] = Form.useForm();
  const [attendanceForm] = Form.useForm();
  const [learningAreaForm] = Form.useForm();
  const [preschoolGradeForm] = Form.useForm();
  const [competenceForm] = Form.useForm();

  const normalizeList = (data) =>
    Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
        ? data.results
        : [];

  const getGradeColor = (grade) => {
    const first = String(grade || "")
      .charAt(0)
      .toUpperCase();
    const colorMap = {
      A: "green",
      B: "blue",
      C: "cyan",
      D: "orange",
      E: "red",
      F: "red",
      P: "purple",
    };
    return colorMap[first] || "default";
  };

  const fetchGradingSystems = useCallback(async () => {
    setGradingLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.academics?.grading_systems,
    );
    setGradingLoading(false);

    if (res?.ok) {
      const systems = normalizeList(res.data);
      setGradingSystems(systems);
      setSelectedGradingSystemId((prev) => {
        if (prev && systems.some((s) => String(s.id) === String(prev)))
          return prev;
        return systems[0]?.id ?? null;
      });
      return;
    }

    message.error(res?.msg || "Failed to load grading systems");
  }, []);

  const fetchGrades = useCallback(async (gradingSystemId) => {
    if (!gradingSystemId) {
      setGrades([]);
      return;
    }

    setGradingLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      `${Settings?.urls?.academics?.grades}?grading_system=${gradingSystemId}`,
    );
    setGradingLoading(false);

    if (res?.ok) {
      setGrades(normalizeList(res.data));
      return;
    }

    message.error(res?.msg || "Failed to load grades");
  }, []);

  const fetchAttendanceRemarks = useCallback(async () => {
    setAttendanceLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.academics?.attendance_remark,
    );
    setAttendanceLoading(false);

    if (res?.ok) {
      setAttendanceRemarks(normalizeList(res.data));
      return;
    }

    message.error(res?.msg || "Failed to load attendance remarks");
  }, []);

  const fetchLearningAreas = useCallback(async () => {
    setPreschoolLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.academics?.preschool_learning_areas,
    );
    setPreschoolLoading(false);

    if (res?.ok) {
      setLearningAreas(normalizeList(res.data));
      return;
    }

    message.error(res?.msg || "Failed to load preschool learning areas");
  }, []);

  const fetchPreschoolGrades = useCallback(async () => {
    setPreschoolLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.academics?.preschool_grades,
    );
    setPreschoolLoading(false);

    if (res?.ok) {
      setPreschoolGrades(normalizeList(res.data));
      return;
    }

    message.error(res?.msg || "Failed to load preschool grades");
  }, []);

  const fetchPreschoolCompetences = useCallback(
    async (overrides = {}) => {
      const classID =
        overrides.classID !== undefined
          ? overrides.classID
          : competenceClassFilter;
      const termID =
        overrides.termID !== undefined
          ? overrides.termID
          : competenceTermFilter;
      const columnID =
        overrides.columnID !== undefined
          ? overrides.columnID
          : competenceColumnFilter;
      const params = new URLSearchParams();
      if (classID) params.append("class_id", classID);
      if (termID) params.append("term_id", termID);
      if (columnID) params.append("column_id", columnID);
      const query = params.toString();
      const url = `${Settings?.urls?.academics?.preschool_competence_settings}${query ? `?${query}` : ""}`;
      setCompetenceLoading(true);
      const res = await Utilities.fetchWithAuth("GET", url);
      setCompetenceLoading(false);

      if (res?.ok) {
        setPreschoolCompetences(normalizeList(res.data));
        return;
      }

      message.error(res?.msg || "Failed to load preschool competences");
    },
    [competenceClassFilter, competenceTermFilter, competenceColumnFilter],
  );

  const clearCompetenceFilters = () => {
    setCompetenceClassFilter(null);
    setCompetenceTermFilter(null);
    setCompetenceColumnFilter(null);
    fetchPreschoolCompetences({ classID: null, termID: null, columnID: null });
  };

  const fetchPreschoolCompetenceOptions = useCallback(async () => {
    const [classesRes, termsRes] = await Promise.all([
      Utilities.fetchWithAuth("GET", Settings?.urls?.bootstrap?.klass),
      Utilities.fetchWithAuth("GET", Settings?.urls?.enrollment?.terms),
    ]);

    if (classesRes?.ok) {
      setPreschoolClasses(
        normalizeList(classesRes.data).filter(
          (item) =>
            String(item?.classGroup ?? item?.class_group).toLowerCase() ===
            "preschool",
        ),
      );
    } else {
      message.error(classesRes?.msg || "Failed to load preschool classes");
    }

    if (termsRes?.ok) {
      setPreschoolTerms(normalizeList(termsRes.data));
    } else {
      message.error(termsRes?.msg || "Failed to load terms");
    }
  }, []);

  useEffect(() => {
    fetchGradingSystems();
    fetchAttendanceRemarks();
    fetchLearningAreas();
    fetchPreschoolGrades();
    fetchPreschoolCompetences();
    fetchPreschoolCompetenceOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchGrades(selectedGradingSystemId);
  }, [selectedGradingSystemId, fetchGrades]);

  const openGradeModal = (record = null) => {
    setEditingGrade(record);
    if (record) {
      gradeForm.setFieldsValue({
        letter_grade: record?.letter_grade,
        numeric_grade: record?.numeric_grade,
        min_score: record?.min_score,
        max_score: record?.max_score,
        remark: record?.remark,
        classTeacherRemark:
          record?.classTeacherRemark ?? record?.class_teacher_remark ?? "",
        headTeacherRemark:
          record?.headTeacherRemark ?? record?.head_teacher_remark ?? "",
      });
    } else {
      gradeForm.resetFields();
    }
    setGradeModalOpen(true);
  };

  const saveGrade = async () => {
    if (!selectedGradingSystemId) {
      message.error("Please select a grading system first");
      return;
    }

    try {
      const values = await gradeForm.validateFields();
      setActionLoading(true);

      let res;
      if (editingGrade?.id) {
        const url = Settings?.urls?.academics?.grade_detail?.replace(
          ":id",
          editingGrade.id,
        );
        res = await Utilities.fetchWithAuth("PATCH", url, values);
      } else {
        const url =
          Settings?.urls?.academics?.grading_system_add_grade?.replace(
            ":id",
            selectedGradingSystemId,
          );
        res = await Utilities.fetchWithAuth("POST", url, values);
      }

      setActionLoading(false);

      if (res?.ok) {
        message.success(
          editingGrade
            ? "Grade updated successfully"
            : "Grade added successfully",
        );
        setGradeModalOpen(false);
        setEditingGrade(null);
        gradeForm.resetFields();
        fetchGrades(selectedGradingSystemId);
        fetchGradingSystems();
        return;
      }

      message.error(res?.msg || "Failed to save grade");
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save grade");
    }
  };

  const deleteGrade = (record) => {
    Modal.confirm({
      title: "Delete Grade",
      content: "Are you sure you want to delete this grade?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const url = Settings?.urls?.academics?.grade_detail?.replace(
          ":id",
          record?.id,
        );
        const res = await Utilities.fetchWithAuth("DELETE", url);
        setActionLoading(false);

        if (res?.ok) {
          message.success("Grade deleted successfully");
          fetchGrades(selectedGradingSystemId);
          fetchGradingSystems();
        } else {
          message.error(res?.msg || "Failed to delete grade");
        }
      },
    });
  };

  const openAttendanceModal = (record = null) => {
    setEditingAttendanceRemark(record);
    attendanceForm.setFieldsValue({ remark: record?.remark || "" });
    setAttendanceModalOpen(true);
  };

  const saveAttendanceRemark = async () => {
    try {
      const values = await attendanceForm.validateFields();
      setActionLoading(true);

      const url = editingAttendanceRemark?.id
        ? `${Settings?.urls?.academics?.attendance_remark}/${editingAttendanceRemark.id}`
        : Settings?.urls?.academics?.attendance_remark;
      const method = editingAttendanceRemark?.id ? "PATCH" : "POST";
      const res = await Utilities.fetchWithAuth(method, url, {
        remark: values.remark,
      });
      setActionLoading(false);

      if (res?.ok) {
        message.success(
          editingAttendanceRemark
            ? "Remark updated successfully"
            : "Remark added successfully",
        );
        setAttendanceModalOpen(false);
        setEditingAttendanceRemark(null);
        attendanceForm.resetFields();
        fetchAttendanceRemarks();
        return;
      }

      message.error(res?.msg || "Failed to save attendance remark");
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save attendance remark");
    }
  };

  const deleteAttendanceRemark = (record) => {
    Modal.confirm({
      title: "Delete Attendance Remark",
      content: "Are you sure you want to delete this remark?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.academics?.attendance_remark}/${record?.id}`,
        );
        setActionLoading(false);

        if (res?.ok) {
          message.success("Remark deleted successfully");
          fetchAttendanceRemarks();
        } else {
          message.error(res?.msg || "Failed to delete attendance remark");
        }
      },
    });
  };

  const openLearningAreaModal = (record = null) => {
    setEditingLearningArea(record);
    learningAreaForm.setFieldsValue({ columnName: record?.columnName || "" });
    setLearningAreaModalOpen(true);
  };

  const saveLearningArea = async () => {
    try {
      const values = await learningAreaForm.validateFields();
      setActionLoading(true);

      const url = editingLearningArea?.id
        ? `${Settings?.urls?.academics?.preschool_learning_areas}/${editingLearningArea.id}`
        : Settings?.urls?.academics?.preschool_learning_areas;
      const method = editingLearningArea?.id ? "PATCH" : "POST";
      const res = await Utilities.fetchWithAuth(method, url, {
        columnName: values.columnName,
      });
      setActionLoading(false);

      if (res?.ok) {
        message.success(
          editingLearningArea
            ? "Learning area updated successfully"
            : "Learning area added successfully",
        );
        setLearningAreaModalOpen(false);
        setEditingLearningArea(null);
        learningAreaForm.resetFields();
        fetchLearningAreas();
        return;
      }

      message.error(res?.msg || "Failed to save learning area");
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save learning area");
    }
  };

  const deleteLearningArea = (record) => {
    Modal.confirm({
      title: "Delete Learning Area",
      content: "Are you sure you want to delete this learning area?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.academics?.preschool_learning_areas}/${record?.id}`,
        );
        setActionLoading(false);

        if (res?.ok) {
          message.success("Learning area deleted successfully");
          fetchLearningAreas();
        } else {
          message.error(res?.msg || "Failed to delete learning area");
        }
      },
    });
  };

  const openPreschoolGradeModal = (record = null) => {
    setEditingPreschoolGrade(record);
    preschoolGradeForm.setFieldsValue({
      letterGrade: record?.letterGrade || "",
      remark: record?.remark || "",
    });
    setPreschoolGradeModalOpen(true);
  };

  const savePreschoolGrade = async () => {
    try {
      const values = await preschoolGradeForm.validateFields();
      setActionLoading(true);

      const url = editingPreschoolGrade?.id
        ? `${Settings?.urls?.academics?.preschool_grades}/${editingPreschoolGrade.id}`
        : Settings?.urls?.academics?.preschool_grades;
      const method = editingPreschoolGrade?.id ? "PATCH" : "POST";
      const res = await Utilities.fetchWithAuth(method, url, values);
      setActionLoading(false);

      if (res?.ok) {
        message.success(
          editingPreschoolGrade
            ? "Preschool grade updated successfully"
            : "Preschool grade added successfully",
        );
        setPreschoolGradeModalOpen(false);
        setEditingPreschoolGrade(null);
        preschoolGradeForm.resetFields();
        fetchPreschoolGrades();
        return;
      }

      message.error(res?.msg || "Failed to save preschool grade");
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save preschool grade");
    }
  };

  const deletePreschoolGrade = (record) => {
    Modal.confirm({
      title: "Delete Preschool Grade",
      content: "Are you sure you want to delete this preschool grade?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.academics?.preschool_grades}/${record?.id}`,
        );
        setActionLoading(false);

        if (res?.ok) {
          message.success("Preschool grade deleted successfully");
          fetchPreschoolGrades();
        } else {
          message.error(res?.msg || "Failed to delete preschool grade");
        }
      },
    });
  };

  const openCompetenceModal = (record = null) => {
    setEditingCompetence(record);
    if (record) {
      competenceForm.setFieldsValue({
        columnID: record?.columnID,
        classID: record?.classID,
        termID: record?.termID,
        statement: record?.statement,
        order: record?.order ?? 0,
      });
    } else {
      competenceForm.resetFields();
      competenceForm.setFieldsValue({ order: 0 });
    }
    setCompetenceModalOpen(true);
  };

  const saveCompetence = async () => {
    try {
      const values = await competenceForm.validateFields();
      setActionLoading(true);

      const url = editingCompetence?.id
        ? `${Settings?.urls?.academics?.preschool_competence_settings}/${editingCompetence.id}`
        : Settings?.urls?.academics?.preschool_competence_settings;
      const method = editingCompetence?.id ? "PATCH" : "POST";
      const res = await Utilities.fetchWithAuth(method, url, values);
      setActionLoading(false);

      if (res?.ok) {
        message.success(
          editingCompetence
            ? "Competence updated successfully"
            : "Competence added successfully",
        );
        setCompetenceModalOpen(false);
        setEditingCompetence(null);
        competenceForm.resetFields();
        fetchPreschoolCompetences();
        return;
      }

      message.error(res?.msg || "Failed to save competence");
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save competence");
    }
  };

  const deleteCompetence = (record) => {
    Modal.confirm({
      title: "Delete Competence",
      content: "Are you sure you want to delete this competence statement?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.academics?.preschool_competence_settings}/${record?.id}`,
        );
        setActionLoading(false);

        if (res?.ok) {
          message.success("Competence deleted successfully");
          fetchPreschoolCompetences();
        } else {
          message.error(res?.msg || "Failed to delete competence");
        }
      },
    });
  };

  const gradeColumns = [
    {
      title: "Grade",
      dataIndex: "letter_grade",
      width: 90,
      render: (value) => <Tag color={getGradeColor(value)}>{value || "-"}</Tag>,
    },
    { title: "Min Score", dataIndex: "min_score", width: 110 },
    { title: "Max Score", dataIndex: "max_score", width: 110 },
    {
      title: "Numeric",
      dataIndex: "numeric_grade",
      width: 90,
      render: (v) => v ?? "-",
    },
    { title: "Remark", dataIndex: "remark", render: (v) => v || "-" },
    {
      title: "Class Teacher Remark",
      dataIndex: "classTeacherRemark",
      render: (v, record) => v || record?.class_teacher_remark || "-",
    },
    {
      title: "Head Teacher Remark",
      dataIndex: "headTeacherRemark",
      render: (v, record) => v || record?.head_teacher_remark || "-",
    },
    {
      title: "",
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Button
            size="small"
            type="link"
            onClick={() => openGradeModal(record)}
          >
            <i className="fas fa-edit" />
          </Button>
          <Button
            size="small"
            type="link"
            danger
            onClick={() => deleteGrade(record)}
          >
            <i className="fas fa-trash" />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <ReportColumnSettings />
      <Row gutter={isLgUp ? 24 : 12}>
        <Col xs={24} lg={14}>
          <SettingsSection
            title="Grading System"
            description="Define grade boundaries and remarks used in report cards and scoresheets."
          >
            <div className="mb-3">
              <Space
                wrap={isLgUp}
                direction={isLgUp ? "horizontal" : "vertical"}
                style={{ width: "100%" }}
              >
                <Select
                  value={selectedGradingSystemId}
                  onChange={(value) => setSelectedGradingSystemId(value)}
                  placeholder="Select grading system"
                  style={isLgUp ? { minWidth: 230 } : { width: "100%" }}
                  options={gradingSystems.map((sys) => ({
                    value: sys.id,
                    label: sys.name,
                  }))}
                  allowClear
                />
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={fetchGradingSystems}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openGradeModal()}
                  disabled={!selectedGradingSystemId}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Add Grade
                </Button>
              </Space>
            </div>
            <ResponsiveTable
              dataSource={grades}
              columns={gradeColumns}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={gradingLoading}
            />
          </SettingsSection>
        </Col>

        <Col xs={24} lg={10}>
          <SettingsSection
            title="Attendance Remarks"
            description="Status codes used when recording daily attendance."
          >
            <div className="mb-2">
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openAttendanceModal()}
                style={!isLgUp ? { width: "100%" } : undefined}
              >
                Add Remark
              </Button>
            </div>
            <ResponsiveTable
              dataSource={attendanceRemarks}
              columns={[
                { title: "Remark", dataIndex: "remark" },
                {
                  title: "",
                  width: 90,
                  render: (_, record) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => openAttendanceModal(record)}
                      >
                        <i className="fas fa-edit" />
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => deleteAttendanceRemark(record)}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </Space>
                  ),
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={attendanceLoading}
            />
          </SettingsSection>

          <SettingsSection
            title="Preschool Learning Areas"
            description="Manage preschool learning area columns."
          >
            <div className="mb-2">
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openLearningAreaModal()}
                style={!isLgUp ? { width: "100%" } : undefined}
              >
                Add Area
              </Button>
            </div>
            <ResponsiveTable
              dataSource={learningAreas}
              columns={[
                { title: "Area", dataIndex: "columnName" },
                {
                  title: "",
                  width: 90,
                  render: (_, record) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => openLearningAreaModal(record)}
                      >
                        <i className="fas fa-edit" />
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => deleteLearningArea(record)}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </Space>
                  ),
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={preschoolLoading}
            />
          </SettingsSection>

          <SettingsSection
            title="Preschool Grades"
            description="Manage preschool grade remarks."
          >
            <div className="mb-2">
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openPreschoolGradeModal()}
                style={!isLgUp ? { width: "100%" } : undefined}
              >
                Add Preschool Grade
              </Button>
            </div>
            <ResponsiveTable
              dataSource={preschoolGrades}
              columns={[
                {
                  title: "Grade",
                  dataIndex: "letterGrade",
                  width: 90,
                  render: (value) => (
                    <Tag color={getGradeColor(value)}>{value || "-"}</Tag>
                  ),
                },
                { title: "Remark", dataIndex: "remark" },
                {
                  title: "",
                  width: 90,
                  render: (_, record) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => openPreschoolGradeModal(record)}
                      >
                        <i className="fas fa-edit" />
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => deletePreschoolGrade(record)}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </Space>
                  ),
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={preschoolLoading}
            />
          </SettingsSection>
        </Col>
      </Row>

      <Row gutter={isLgUp ? 24 : 12}>
        <Col xs={24}>
          <SettingsSection
            title="Preschool Competences"
            description="Manage competence statements by learning area, preschool class, and term."
          >
            <div className="mb-2">
              <Space wrap>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openCompetenceModal()}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Add Competence
                </Button>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => fetchPreschoolCompetences()}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Refresh
                </Button>
                {(competenceClassFilter ||
                  competenceTermFilter ||
                  competenceColumnFilter) && (
                  <Button size="small" onClick={clearCompetenceFilters}>
                    Clear Filters
                  </Button>
                )}
              </Space>
            </div>
            <ResponsiveTable
              dataSource={preschoolCompetences}
              columns={[
                {
                  title: "Learning Area",
                  dataIndex: "columnName",
                  width: 180,
                  filterDropdown: ({ confirm }) => (
                    <div style={{ padding: 8 }}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        placeholder="Select learning area"
                        style={{ width: 220 }}
                        value={competenceColumnFilter}
                        onChange={(value) => {
                          const id = value ?? null;
                          setCompetenceColumnFilter(id);
                          fetchPreschoolCompetences({
                            classID: competenceClassFilter,
                            termID: competenceTermFilter,
                            columnID: id,
                          });
                          confirm();
                        }}
                        options={learningAreas.map((area) => ({
                          value: area.id,
                          label: area.columnName,
                        }))}
                      />
                    </div>
                  ),
                  filterIcon: () => (
                    <FilterOutlined
                      style={{
                        color: competenceColumnFilter ? "#1677ff" : undefined,
                      }}
                    />
                  ),
                  render: (value, record) =>
                    value ||
                    learningAreas.find(
                      (area) => String(area?.id) === String(record?.columnID),
                    )?.columnName ||
                    "-",
                },
                {
                  title: "Class",
                  dataIndex: "classID",
                  width: 160,
                  filterDropdown: ({ confirm }) => (
                    <div style={{ padding: 8 }}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        placeholder="Select class"
                        style={{ width: 200 }}
                        value={competenceClassFilter}
                        onChange={(value) => {
                          const id = value ?? null;
                          setCompetenceClassFilter(id);
                          fetchPreschoolCompetences({
                            classID: id,
                            termID: competenceTermFilter,
                            columnID: competenceColumnFilter,
                          });
                          confirm();
                        }}
                        options={preschoolClasses.map((item) => ({
                          value: item.id,
                          label: item.name,
                        }))}
                      />
                    </div>
                  ),
                  filterIcon: () => (
                    <FilterOutlined
                      style={{
                        color: competenceClassFilter ? "#1677ff" : undefined,
                      }}
                    />
                  ),
                  render: (value) =>
                    preschoolClasses.find(
                      (item) => String(item?.id) === String(value),
                    )?.name ||
                    value ||
                    "-",
                },
                {
                  title: "Term",
                  dataIndex: "termID",
                  width: 120,
                  filterDropdown: ({ confirm }) => (
                    <div style={{ padding: 8 }}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        placeholder="Select term"
                        style={{ width: 200 }}
                        value={competenceTermFilter}
                        onChange={(value) => {
                          const id = value ?? null;
                          setCompetenceTermFilter(id);
                          fetchPreschoolCompetences({
                            classID: competenceClassFilter,
                            termID: id,
                            columnID: competenceColumnFilter,
                          });
                          confirm();
                        }}
                        options={preschoolTerms.map((item) => ({
                          value: item.id,
                          label: item.name,
                        }))}
                      />
                    </div>
                  ),
                  filterIcon: () => (
                    <FilterOutlined
                      style={{
                        color: competenceTermFilter ? "#1677ff" : undefined,
                      }}
                    />
                  ),
                  render: (value) =>
                    preschoolTerms.find(
                      (item) => String(item?.id) === String(value),
                    )?.name ||
                    value ||
                    "-",
                },
                { title: "Statement", dataIndex: "statement", width: 360 },
                { title: "Order", dataIndex: "order", width: 90 },
                {
                  title: "",
                  width: 90,
                  fixed: "right",
                  render: (_, record) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => openCompetenceModal(record)}
                      >
                        <i className="fas fa-edit" />
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => deleteCompetence(record)}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </Space>
                  ),
                },
              ]}
              rowKey="id"
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              size="small"
              bordered
              loading={competenceLoading}
            />
          </SettingsSection>
        </Col>
      </Row>

      <Divider dashed />

      <Row gutter={isLgUp ? 24 : 12}>
        <Col xs={24}>
          <SignatureSettings />
        </Col>
      </Row>

      <Modal
        title={editingGrade ? "Edit Grade" : "Add Grade"}
        open={gradeModalOpen}
        onCancel={() => {
          setGradeModalOpen(false);
          setEditingGrade(null);
          gradeForm.resetFields();
        }}
        onOk={saveGrade}
        okText={editingGrade ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={gradeForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="letter_grade"
                label="Letter Grade"
                rules={[
                  { required: true, message: "Letter grade is required" },
                ]}
              >
                <Input maxLength={4} placeholder="e.g. A" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="numeric_grade"
                label="Numeric Grade"
                rules={[
                  { required: true, message: "Numeric grade is required" },
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="min_score"
                label="Min Score"
                rules={[
                  { required: true, message: "Min score is required" },
                  {
                    type: "number",
                    min: 0,
                    max: 100,
                    message: "Min must be 0-100",
                  },
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="max_score"
                label="Max Score"
                rules={[
                  { required: true, message: "Max score is required" },
                  {
                    type: "number",
                    min: 0,
                    max: 100,
                    message: "Max must be 0-100",
                  },
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} max={100} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="Remark">
            <Input placeholder="e.g. Excellent" />
          </Form.Item>
          <Form.Item name="classTeacherRemark" label="Class Teacher Remark">
            <TextArea
              rows={2}
              maxLength={100}
              showCount
              placeholder="e.g. Keep up the excellent work"
            />
          </Form.Item>
          <Form.Item name="headTeacherRemark" label="Head Teacher Remark">
            <TextArea
              rows={2}
              maxLength={100}
              showCount
              placeholder="e.g. Outstanding performance"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          editingAttendanceRemark
            ? "Edit Attendance Remark"
            : "Add Attendance Remark"
        }
        open={attendanceModalOpen}
        onCancel={() => {
          setAttendanceModalOpen(false);
          setEditingAttendanceRemark(null);
          attendanceForm.resetFields();
        }}
        onOk={saveAttendanceRemark}
        okText={editingAttendanceRemark ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={attendanceForm} layout="vertical">
          <Form.Item
            name="remark"
            label="Remark"
            rules={[{ required: true, message: "Remark is required" }]}
          >
            <Input placeholder="e.g. Late" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingLearningArea ? "Edit Learning Area" : "Add Learning Area"}
        open={learningAreaModalOpen}
        onCancel={() => {
          setLearningAreaModalOpen(false);
          setEditingLearningArea(null);
          learningAreaForm.resetFields();
        }}
        onOk={saveLearningArea}
        okText={editingLearningArea ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={learningAreaForm} layout="vertical">
          <Form.Item
            name="columnName"
            label="Area"
            rules={[{ required: true, message: "Area name is required" }]}
          >
            <Input placeholder="e.g. Numeracy" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          editingPreschoolGrade ? "Edit Preschool Grade" : "Add Preschool Grade"
        }
        open={preschoolGradeModalOpen}
        onCancel={() => {
          setPreschoolGradeModalOpen(false);
          setEditingPreschoolGrade(null);
          preschoolGradeForm.resetFields();
        }}
        onOk={savePreschoolGrade}
        okText={editingPreschoolGrade ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={preschoolGradeForm} layout="vertical">
          <Form.Item
            name="letterGrade"
            label="Grade"
            rules={[{ required: true, message: "Grade is required" }]}
          >
            <Input placeholder="e.g. E" />
          </Form.Item>
          <Form.Item
            name="remark"
            label="Remark"
            rules={[{ required: true, message: "Remark is required" }]}
          >
            <Input placeholder="e.g. Excellent" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          editingCompetence
            ? "Edit Preschool Competence"
            : "Add Preschool Competence"
        }
        open={competenceModalOpen}
        onCancel={() => {
          setCompetenceModalOpen(false);
          setEditingCompetence(null);
          competenceForm.resetFields();
        }}
        onOk={saveCompetence}
        okText={editingCompetence ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
        width={700}
      >
        <Form form={competenceForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="columnID"
                label="Learning Area"
                rules={[
                  { required: true, message: "Learning area is required" },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select learning area"
                  options={learningAreas.map((area) => ({
                    value: area.id,
                    label: area.columnName,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="classID"
                label="Preschool Class"
                rules={[{ required: true, message: "Class is required" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select class"
                  options={preschoolClasses.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="termID"
                label="Term"
                rules={[{ required: true, message: "Term is required" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select term"
                  options={preschoolTerms.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="order"
                label="Display Order"
                rules={[
                  { required: true, message: "Display order is required" },
                ]}
              >
                <InputNumber min={0} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="statement"
            label="Competence Statement"
            rules={[
              { required: true, message: "Competence statement is required" },
            ]}
          >
            <TextArea rows={4} placeholder="Enter the competence statement" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  3. ACADEMIC CALENDAR
// ═══════════════════════════════════════════════════════════════════════════════
const AcademicCalendarSettings = () => {
  const screens = useBreakpoint();
  const isLgUp = !!screens.lg;
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYearChoices, setAcademicYearChoices] = useState([]);
  const [terms, setTerms] = useState([]);
  const [academicYearLoading, setAcademicYearLoading] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [termModalOpen, setTermModalOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [errorStack, setErrorStack] = useState(null);
  const [yearForm] = Form.useForm();
  const [termForm] = Form.useForm();

  const normalizeList = (data) =>
    Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
        ? data.results
        : [];

  const fetchAcademicYears = useCallback(async () => {
    setAcademicYearLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.enrollment?.academicYears,
    );
    setAcademicYearLoading(false);

    if (res?.ok) {
      setAcademicYears(normalizeList(res?.data));
      return;
    }

    message.error(res?.msg || "Failed to load academic years");
  }, []);

  const fetchAcademicYearChoices = useCallback(async () => {
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.enrollment?.academicYearsChoices,
    );
    if (res?.ok) {
      setAcademicYearChoices(normalizeList(res?.data));
      return;
    }
    message.error(res?.msg || "Failed to load academic year choices");
  }, []);

  const fetchTerms = useCallback(async () => {
    setTermsLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.enrollment?.terms,
    );
    setTermsLoading(false);

    if (res?.ok) {
      setTerms(normalizeList(res?.data));
      return;
    }

    message.error(res?.msg || "Failed to load terms");
  }, []);

  useEffect(() => {
    fetchAcademicYears();
    fetchAcademicYearChoices();
    fetchTerms();
  }, [fetchAcademicYears, fetchAcademicYearChoices, fetchTerms]);

  const setCurrentAcademicYear = async (yearId) => {
    const currentYear = academicYears.find(
      (item) =>
        Number(item?.is_current) === 1 && String(item?.id) !== String(yearId),
    );

    if (currentYear?.id) {
      const clearRes = await Utilities.fetchWithAuth(
        "PATCH",
        `${Settings?.urls?.enrollment?.academicYears}/${currentYear.id}`,
        { is_current: 0 },
      );
      if (!clearRes?.ok) {
        message.error(
          clearRes?.msg || "Failed to clear previous current academic year",
        );
        return false;
      }
    }

    const res = await Utilities.fetchWithAuth(
      "PATCH",
      `${Settings?.urls?.enrollment?.academicYears}/${yearId}`,
      { is_current: 1 },
    );

    if (!res?.ok) {
      message.error(res?.msg || "Failed to set current academic year");
      return false;
    }

    return true;
  };

  const getAcademicYearStatus = (record) => {
    if (Number(record?.is_current) === 1)
      return { label: "Active", color: "green" };
    if (Number(record?.archive) === 1)
      return { label: "Archived", color: "default" };
    return { label: "Inactive", color: "default" };
  };

  const getTermStatus = (record) => {
    if (Number(record?.isCurrent) === 1)
      return { label: "Active", color: "blue" };
    const today = dayjs().startOf("day");
    const start = record?.start ? dayjs(record.start) : null;
    const end = record?.end ? dayjs(record.end) : null;
    if (start && start.isAfter(today, "day"))
      return { label: "Upcoming", color: "default" };
    if (end && end.isBefore(today, "day"))
      return { label: "Completed", color: "default" };
    return { label: "Inactive", color: "default" };
  };

  const openAcademicYearModal = (record = null) => {
    setEditingYear(record);
    if (record) {
      yearForm.setFieldsValue({
        acadyr: record?.acadyr,
        archive: Number(record?.archive) === 1,
        is_current: Number(record?.is_current) === 1,
      });
    } else {
      yearForm.resetFields();
      yearForm.setFieldsValue({
        archive: false,
        is_current: false,
      });
    }
    setYearModalOpen(true);
  };

  const saveAcademicYear = async () => {
    try {
      const values = await yearForm.validateFields();
      setActionLoading(true);
      const payload = {
        acadyr: values.acadyr,
        archive: values.archive ? 1 : 0,
        is_current: values.is_current ? 1 : 0,
      };

      const isEditing = !!editingYear?.id;
      const url = isEditing
        ? `${Settings?.urls?.enrollment?.academicYears}/${editingYear.id}`
        : Settings?.urls?.enrollment?.academicYears;
      const method = isEditing ? "PATCH" : "POST";
      const res = await Utilities.fetchWithAuth(method, url, payload);

      if (!res?.ok) {
        setActionLoading(false);
        message.error(res?.msg || "Failed to save academic year");
        return;
      }

      const yearId = isEditing ? editingYear.id : res?.data?.id;
      if (values.is_current && yearId) {
        const updated = await setCurrentAcademicYear(yearId);
        if (!updated) {
          setActionLoading(false);
          return;
        }
      }

      setActionLoading(false);
      message.success(
        isEditing
          ? "Academic year updated successfully"
          : "Academic year added successfully",
      );
      setYearModalOpen(false);
      setEditingYear(null);
      yearForm.resetFields();
      fetchAcademicYears();
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save academic year");
    }
  };

  const deleteAcademicYear = (record) => {
    Modal.confirm({
      title: "Delete Academic Year",
      content: "Are you sure you want to delete this academic year?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.enrollment?.academicYears}/${record?.id}`,
        );
        setActionLoading(false);

        if (res?.ok) {
          message.success("Academic year deleted successfully");
          fetchAcademicYears();
        } else {
          message.error(res?.msg || "Failed to delete academic year");
        }
      },
    });
  };

  const makeCurrentAcademicYear = async (record) => {
    if (Number(record?.is_current) === 1) return;
    setActionLoading(true);
    const updated = await setCurrentAcademicYear(record?.id);
    setActionLoading(false);
    if (updated) {
      message.success("Academic year set as current");
      fetchAcademicYears();
    }
  };

  const openTermModal = (record = null) => {
    setEditingTerm(record);
    if (record) {
      termForm.setFieldsValue({
        name: record?.name || "",
        start: record?.start ? dayjs(record.start) : null,
        end: record?.end ? dayjs(record.end) : null,
      });
    } else {
      termForm.resetFields();
    }
    setTermModalOpen(true);
  };

  const saveTerm = async () => {
    try {
      const values = await termForm.validateFields();
      setActionLoading(true);
      const payload = {
        name: values.name,
        start: values.start ? values.start.format("YYYY-MM-DD") : null,
        end: values.end ? values.end.format("YYYY-MM-DD") : null,
      };

      const isEditing = !!editingTerm?.id;
      const url = isEditing
        ? `${Settings?.urls?.enrollment?.terms}/${editingTerm.id}`
        : Settings?.urls?.enrollment?.terms;
      const method = isEditing ? "PATCH" : "POST";
      const res = await Utilities.fetchWithAuth(
        method,
        url,
        isEditing ? payload : { ...payload, isCurrent: 0 },
      );

      setActionLoading(false);
      if (res?.ok) {
        message.success(
          isEditing ? "Term updated successfully" : "Term added successfully",
        );
        setTermModalOpen(false);
        setEditingTerm(null);
        termForm.resetFields();
        fetchTerms();
        return;
      }

      message.error(res?.msg || "Failed to save term");
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save term");
    }
  };

  const deleteTerm = (record) => {
    Modal.confirm({
      title: "Delete Term",
      content: "Are you sure you want to delete this term?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.enrollment?.terms}/${record?.id}`,
        );
        setActionLoading(false);

        if (res?.ok) {
          message.success("Term deleted successfully");
          fetchTerms();
        } else {
          message.error(res?.msg || "Failed to delete term");
        }
      },
    });
  };

  const performTermSwitch = async (record) => {
    if (Number(record?.isCurrent) === 1) return;
    setErrorStack(null);
    setActionLoading(true);
    const res = await Utilities.fetchWithAuth(
      "POST",
      `${Settings?.urls?.enrollment?.terms}/${record?.id}/set-current`,
    );
    setActionLoading(false);

    if (res?.ok) {
      const reconciliation = res?.data?.reconciliation || {};
      const studentsUpdated = Number(reconciliation?.studentsUpdated || 0);
      const feesAssigned = Number(reconciliation?.feesAssigned || 0);
      message.success(
        `Term set as current. ${studentsUpdated} student${studentsUpdated === 1 ? "" : "s"} updated, ${feesAssigned} fee${feesAssigned === 1 ? "" : "s"} assigned.`,
      );
      fetchTerms();
      return;
    }

    const body = res?.data;
    const rawMsg = res?.msg;

    let errors = null;
    if (Array.isArray(body)) {
      errors = body;
    } else if (body && typeof body === "object") {
      const arr = body?.error ?? body?.errors;
      errors = Array.isArray(arr) ? arr : arr ? [arr] : null;
    } else if (typeof rawMsg === "string" && rawMsg) {
      errors = rawMsg
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
    }

    if (errors?.length) {
      setErrorStack(errors);
    } else {
      message.error(rawMsg || "Failed to set current term");
    }
  };

  const setCurrentTerm = (record) => {
    if (Number(record?.isCurrent) === 1) return;
    if (!record?.start || !record?.end) {
      message.warning("Set both term dates before making this term current.");
      openTermModal(record);
      return;
    }

    Modal.confirm({
      title: `Make ${record?.name || "this term"} current?`,
      content: (
        <div>
          <div>
            {record.start} to {record.end}
          </div>
          <Text type="secondary">
            Students, applicable fees, and missing teacher assignments will be
            carried into this term. Existing target-term assignments are kept.
          </Text>
        </div>
      ),
      okText: "Switch term",
      async onOk() {
        await performTermSwitch(record);
      },
    });
  };

  return (
    <div>
      <Row gutter={isLgUp ? 24 : 12}>
        <Col xs={24} lg={12}>
          <SettingsSection
            title="Academic Years"
            description="Manage academic years. Only one year can be active at a time."
          >
            <div className="mb-2">
              <Space
                wrap={isLgUp}
                direction={isLgUp ? "horizontal" : "vertical"}
                style={{ width: "100%" }}
              >
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={fetchAcademicYears}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openAcademicYearModal()}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Add Academic Year
                </Button>
              </Space>
            </div>
            <ResponsiveTable
              dataSource={academicYears}
              columns={[
                {
                  title: "Year",
                  dataIndex: "acadyr",
                  render: (value, record) => (
                    <>
                      {value || "-"}{" "}
                      {Number(record?.is_current) === 1 && (
                        <Tag color="green">Current</Tag>
                      )}
                    </>
                  ),
                },
                {
                  title: "Status",
                  render: (_, record) => {
                    const status = getAcademicYearStatus(record);
                    return <Tag color={status.color}>{status.label}</Tag>;
                  },
                },
                {
                  title: "",
                  width: 140,
                  render: (_, record) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => openAcademicYearModal(record)}
                      >
                        <i className="fas fa-edit" />
                      </Button>
                      {Number(record?.is_current) !== 1 && (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => makeCurrentAcademicYear(record)}
                          loading={actionLoading}
                        >
                          <i className="fas fa-check" />
                        </Button>
                      )}
                      {Number(record?.is_current) !== 1 && (
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() => deleteAcademicYear(record)}
                        >
                          <i className="fas fa-trash" />
                        </Button>
                      )}
                    </Space>
                  ),
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={academicYearLoading}
            />
          </SettingsSection>
        </Col>

        <Col xs={24} lg={12}>
          <SettingsSection
            title="Terms / Semesters"
            description="Define term periods within the active academic year."
          >
            <div className="mb-2">
              <Space
                wrap={isLgUp}
                direction={isLgUp ? "horizontal" : "vertical"}
                style={{ width: "100%" }}
              >
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={fetchTerms}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openTermModal()}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Add Term
                </Button>
              </Space>
            </div>
            <ResponsiveTable
              dataSource={terms}
              columns={[
                {
                  title: "Term",
                  dataIndex: "name",
                  render: (value, record) => (
                    <>
                      {value || "-"}{" "}
                      {Number(record?.isCurrent) === 1 && (
                        <Badge status="processing" />
                      )}
                    </>
                  ),
                },
                {
                  title: "Start",
                  dataIndex: "start",
                  render: (value) => value || "-",
                },
                {
                  title: "End",
                  dataIndex: "end",
                  render: (value) => value || "-",
                },
                {
                  title: "Status",
                  render: (_, record) => {
                    const status = getTermStatus(record);
                    return <Tag color={status.color}>{status.label}</Tag>;
                  },
                },
                {
                  title: "",
                  width: 140,
                  render: (_, record) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => openTermModal(record)}
                      >
                        <i className="fas fa-edit" />
                      </Button>
                      {Number(record?.isCurrent) !== 1 && (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => setCurrentTerm(record)}
                          loading={actionLoading}
                        >
                          <i className="fas fa-check" />
                        </Button>
                      )}
                      {Number(record?.isCurrent) !== 1 && (
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() => deleteTerm(record)}
                        >
                          <i className="fas fa-trash" />
                        </Button>
                      )}
                    </Space>
                  ),
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={termsLoading}
            />
          </SettingsSection>
        </Col>
      </Row>

      <Modal
        title={editingYear ? "Edit Academic Year" : "Add Academic Year"}
        open={yearModalOpen}
        onCancel={() => {
          setYearModalOpen(false);
          setEditingYear(null);
          yearForm.resetFields();
        }}
        onOk={saveAcademicYear}
        okText={editingYear ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={yearForm} layout="vertical">
          <Form.Item
            name="acadyr"
            label="Academic Year"
            rules={[{ required: true, message: "Academic year is required" }]}
          >
            {academicYearChoices?.length ? (
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select academic year"
                options={academicYearChoices.map((choice) => ({
                  value: choice?.value,
                  label: choice?.label,
                }))}
              />
            ) : (
              <Input placeholder="e.g. 2026" />
            )}
          </Form.Item>
          <Form.Item name="archive" label="Archive" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="is_current"
            label="Set as Current"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingTerm ? "Edit Term" : "Add Term"}
        open={termModalOpen}
        onCancel={() => {
          setTermModalOpen(false);
          setEditingTerm(null);
          termForm.resetFields();
        }}
        onOk={saveTerm}
        okText={editingTerm ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={termForm} layout="vertical">
          <Form.Item
            name="name"
            label="Term Name"
            rules={[{ required: true, message: "Term name is required" }]}
          >
            <Input maxLength={25} placeholder="e.g. Term 1" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="start"
                label="Start Date"
                rules={[{ required: true, message: "Start date is required" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="end"
                label="End Date"
                dependencies={["start"]}
                rules={[
                  { required: true, message: "End date is required" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue("start");
                      if (
                        !value ||
                        !startDate ||
                        !value.isBefore(startDate, "day")
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("End date must be on or after start date"),
                      );
                    },
                  }),
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <ErrorDeck
        messages={errorStack || []}
        onDismiss={() => setErrorStack(null)}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  4. ADMISSIONS SETTINGS
// ═══════════════════════════════════════════════════════════════════════════════
const AdmissionsSettings = () => {
  const screens = useBreakpoint();
  const isLgUp = !!screens.lg;
  const [statuses, setStatuses] = useState([]);
  const [statusTransitions, setStatusTransitions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [transitionModalOpen, setTransitionModalOpen] = useState(false);
  const [editingTransition, setEditingTransition] = useState(null);
  const [statusForm] = Form.useForm();
  const [transitionForm] = Form.useForm();

  const normalizeList = (data) =>
    Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
        ? data.results
        : [];

  const getStatusColor = (value) => {
    const key = String(value || "").toUpperCase();
    const colors = {
      DRAFT: "default",
      SUBMITTED: "blue",
      VERIFIED: "cyan",
      INTERVIEW: "purple",
      ASSESSMENT: "geekblue",
      ADMITTED: "green",
      REJECTED: "red",
      "WAITING LIST": "orange",
    };
    return colors[key] || "default";
  };

  const fetchStatuses = useCallback(async () => {
    setStatusLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.bootstrap?.status,
    );
    setStatusLoading(false);

    if (res?.ok) {
      setStatuses(normalizeList(res?.data));
      return;
    }

    message.error(res?.msg || "Failed to load admission statuses");
  }, []);

  const fetchStatusTransitions = useCallback(async () => {
    setTransitionLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.bootstrap?.statusTransition,
    );
    setTransitionLoading(false);

    if (res?.ok) {
      setStatusTransitions(normalizeList(res?.data));
      return;
    }

    message.error(res?.msg || "Failed to load status transitions");
  }, []);

  const fetchClasses = useCallback(async () => {
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.bootstrap?.klass,
    );
    if (res?.ok) {
      setClasses(normalizeList(res?.data));
      return;
    }
    message.error(res?.msg || "Failed to load classes");
  }, []);

  useEffect(() => {
    fetchStatuses();
    fetchStatusTransitions();
    fetchClasses();
  }, [fetchStatuses, fetchStatusTransitions, fetchClasses]);

  const statusChoiceOptions = Array.from(
    new Set(
      [
        ...statuses.map((item) => item?.status),
        ...statusTransitions.map((item) => item?.current_status),
        ...statusTransitions.map((item) => item?.can_change_to),
      ].filter(Boolean),
    ),
  ).map((value) => ({ value, label: value }));

  const openStatusModal = (record = null) => {
    setEditingStatus(record);
    if (record) {
      statusForm.setFieldsValue({
        status: record?.status,
        lock_form: Number(record?.lock_form) === 1,
        show_score_btn: Number(record?.show_score_btn) === 1,
      });
    } else {
      statusForm.resetFields();
      statusForm.setFieldsValue({
        lock_form: false,
        show_score_btn: false,
      });
    }
    setStatusModalOpen(true);
  };

  const saveStatus = async () => {
    try {
      const values = await statusForm.validateFields();
      setActionLoading(true);
      const payload = {
        status: values.status,
        lock_form: values.lock_form ? 1 : 0,
        show_score_btn: values.show_score_btn ? 1 : 0,
      };

      const isEditing = !!editingStatus?.id;
      const url = isEditing
        ? `${Settings?.urls?.bootstrap?.status}/${editingStatus.id}`
        : Settings?.urls?.bootstrap?.status;
      const method = isEditing ? "PATCH" : "POST";
      const res = await Utilities.fetchWithAuth(method, url, payload);
      setActionLoading(false);

      if (res?.ok) {
        message.success(
          isEditing
            ? "Status updated successfully"
            : "Status added successfully",
        );
        setStatusModalOpen(false);
        setEditingStatus(null);
        statusForm.resetFields();
        fetchStatuses();
        return;
      }

      message.error(res?.msg || "Failed to save status");
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save status");
    }
  };

  const deleteStatus = (record) => {
    Modal.confirm({
      title: "Delete Status",
      content: "Are you sure you want to delete this status?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.bootstrap?.status}/${record?.id}`,
        );
        setActionLoading(false);

        if (res?.ok) {
          message.success("Status deleted successfully");
          fetchStatuses();
          fetchStatusTransitions();
        } else {
          message.error(res?.msg || "Failed to delete status");
        }
      },
    });
  };

  const openTransitionModal = (record = null) => {
    setEditingTransition(record);
    if (record) {
      transitionForm.setFieldsValue({
        klassID: record?.klassID,
        current_status: record?.current_status,
        can_change_to: record?.can_change_to,
      });
    } else {
      transitionForm.resetFields();
    }
    setTransitionModalOpen(true);
  };

  const saveTransition = async () => {
    try {
      const values = await transitionForm.validateFields();
      setActionLoading(true);
      const payload = {
        klassID: values.klassID,
        current_status: values.current_status,
        can_change_to: values.can_change_to,
      };

      const isEditing = !!editingTransition?.id;
      const url = isEditing
        ? `${Settings?.urls?.bootstrap?.statusTransition}/${editingTransition.id}`
        : Settings?.urls?.bootstrap?.statusTransition;
      const method = isEditing ? "PATCH" : "POST";
      const res = await Utilities.fetchWithAuth(method, url, payload);
      setActionLoading(false);

      if (res?.ok) {
        message.success(
          isEditing
            ? "Transition updated successfully"
            : "Transition added successfully",
        );
        setTransitionModalOpen(false);
        setEditingTransition(null);
        transitionForm.resetFields();
        fetchStatusTransitions();
        return;
      }

      message.error(res?.msg || "Failed to save transition");
    } catch (error) {
      if (error?.errorFields) return;
      setActionLoading(false);
      message.error("Failed to save transition");
    }
  };

  const deleteTransition = (record) => {
    Modal.confirm({
      title: "Delete Transition",
      content: "Are you sure you want to delete this transition?",
      okText: "Delete",
      okType: "danger",
      async onOk() {
        setActionLoading(true);
        const res = await Utilities.fetchWithAuth(
          "DELETE",
          `${Settings?.urls?.bootstrap?.statusTransition}/${record?.id}`,
        );
        setActionLoading(false);

        if (res?.ok) {
          message.success("Transition deleted successfully");
          fetchStatusTransitions();
        } else {
          message.error(res?.msg || "Failed to delete transition");
        }
      },
    });
  };

  return (
    <div>
      <Row gutter={isLgUp ? 24 : 12}>
        <Col xs={24} lg={12}>
          <SettingsSection
            title="Admission Statuses"
            description="Manage admission statuses and related flags."
          >
            <div className="mb-2">
              <Space
                wrap={isLgUp}
                direction={isLgUp ? "horizontal" : "vertical"}
                style={{ width: "100%" }}
              >
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={fetchStatuses}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openStatusModal()}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Add Status
                </Button>
              </Space>
            </div>
            <ResponsiveTable
              dataSource={statuses}
              columns={[
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (value) => (
                    <Tag color={getStatusColor(value)}>{value || "-"}</Tag>
                  ),
                },
                {
                  title: "Lock Form",
                  dataIndex: "lock_form",
                  width: 100,
                  render: (value) =>
                    Number(value) === 1 ? (
                      <Tag color="orange">Yes</Tag>
                    ) : (
                      <Tag>No</Tag>
                    ),
                },
                {
                  title: "Show Scores",
                  dataIndex: "show_score_btn",
                  width: 120,
                  render: (value) =>
                    Number(value) === 1 ? (
                      <Tag color="blue">Yes</Tag>
                    ) : (
                      <Tag>No</Tag>
                    ),
                },
                {
                  title: "",
                  width: 90,
                  render: (_, record) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => openStatusModal(record)}
                      >
                        <i className="fas fa-edit" />
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => deleteStatus(record)}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </Space>
                  ),
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={statusLoading}
            />
          </SettingsSection>
        </Col>

        <Col xs={24} lg={12}>
          <SettingsSection
            title="Class Status Transitions"
            description="Define allowed status transitions per class."
          >
            <div className="mb-2">
              <Space
                wrap={isLgUp}
                direction={isLgUp ? "horizontal" : "vertical"}
                style={{ width: "100%" }}
              >
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={fetchStatusTransitions}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openTransitionModal()}
                  style={!isLgUp ? { width: "100%" } : undefined}
                >
                  Add Transition
                </Button>
              </Space>
            </div>
            <ResponsiveTable
              dataSource={statusTransitions}
              columns={[
                {
                  title: "Class",
                  dataIndex: "klass",
                  render: (value) => value || "-",
                },
                {
                  title: "Current Status",
                  dataIndex: "current_status",
                  render: (value) => (
                    <Tag color={getStatusColor(value)}>{value || "-"}</Tag>
                  ),
                },
                {
                  title: "Change To",
                  dataIndex: "can_change_to",
                  render: (value) => (
                    <Tag color={getStatusColor(value)}>{value || "-"}</Tag>
                  ),
                },
                {
                  title: "",
                  width: 90,
                  render: (_, record) => (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => openTransitionModal(record)}
                      >
                        <i className="fas fa-edit" />
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => deleteTransition(record)}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </Space>
                  ),
                },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              loading={transitionLoading}
            />
          </SettingsSection>
        </Col>
      </Row>

      <Modal
        title={editingStatus ? "Edit Admission Status" : "Add Admission Status"}
        open={statusModalOpen}
        onCancel={() => {
          setStatusModalOpen(false);
          setEditingStatus(null);
          statusForm.resetFields();
        }}
        onOk={saveStatus}
        okText={editingStatus ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={statusForm} layout="vertical">
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Status is required" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select status"
              options={statusChoiceOptions}
            />
          </Form.Item>
          <Form.Item name="lock_form" label="Lock Form" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="show_score_btn"
            label="Show Scores Button"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          editingTransition ? "Edit Status Transition" : "Add Status Transition"
        }
        open={transitionModalOpen}
        onCancel={() => {
          setTransitionModalOpen(false);
          setEditingTransition(null);
          transitionForm.resetFields();
        }}
        onOk={saveTransition}
        okText={editingTransition ? "Update" : "Add"}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={transitionForm} layout="vertical">
          <Form.Item
            name="klassID"
            label="Class"
            rules={[{ required: true, message: "Class is required" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select class"
              options={classes.map((item) => ({
                value: item?.id,
                label: item?.name,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="current_status"
            label="Current Status"
            rules={[{ required: true, message: "Current status is required" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select current status"
              options={statusChoiceOptions}
            />
          </Form.Item>
          <Form.Item
            name="can_change_to"
            label="Change To"
            rules={[{ required: true, message: "Target status is required" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select target status"
              options={statusChoiceOptions}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  6. TRANSPORT SETTINGS
// ═══════════════════════════════════════════════════════════════════════════════
const TransportSettings = () => {
  const screens = useBreakpoint();
  const isLgUp = !!screens.lg;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingMeta, setSettingMeta] = useState({
    couponExpiryDays: null,
    fuel_litres_budget_ceiling: null,
  });

  const toPositiveNumber = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
  };

  const fetchSettingByProperty = useCallback(async (property) => {
    const url = `${Settings?.urls?.enrollment?.settings}/by-property?property=${encodeURIComponent(property)}`;
    const res = await Utilities.fetchWithAuth("GET", url);
    if (res?.ok) return res?.data || null;
    if (res?.status === 404) return null;
    throw new Error(res?.msg || `Failed to load ${property}`);
  }, []);

  const loadTransportSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [couponExpirySetting, fuelCeilingSetting] = await Promise.all([
        fetchSettingByProperty("couponExpiryDays"),
        fetchSettingByProperty("fuel_litres_budget_ceiling"),
      ]);

      setSettingMeta({
        couponExpiryDays: couponExpirySetting,
        fuel_litres_budget_ceiling: fuelCeilingSetting,
      });

      form.setFieldsValue({
        couponExpiryDays: toPositiveNumber(couponExpirySetting?.value, 30),
        fuel_litres_budget_ceiling: toPositiveNumber(
          fuelCeilingSetting?.value,
          200,
        ),
      });
    } catch (error) {
      message.error(error?.message || "Failed to load transport settings");
    } finally {
      setLoading(false);
    }
  }, [fetchSettingByProperty, form]);

  useEffect(() => {
    loadTransportSettings();
  }, [loadTransportSettings]);

  const savePropertySetting = async ({
    property,
    value,
    meta,
    description,
  }) => {
    if (meta?.id) {
      return Utilities.fetchWithAuth(
        "PATCH",
        `${Settings?.urls?.enrollment?.settings}/${meta.id}`,
        { value: String(value) },
      );
    }

    return Utilities.fetchWithAuth(
      "POST",
      Settings?.urls?.enrollment?.settings,
      {
        property,
        value: String(value),
        is_public: 1,
        description,
      },
    );
  };

  const saveTransportSettings = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const updates = [
        {
          property: "couponExpiryDays",
          value: values.couponExpiryDays,
          meta: settingMeta.couponExpiryDays,
          description: "Number of days before generated fuel coupons expire",
        },
        {
          property: "fuel_litres_budget_ceiling",
          value: values.fuel_litres_budget_ceiling,
          meta: settingMeta.fuel_litres_budget_ceiling,
          description:
            "Fuel litres ceiling above which requests are marked exceptional",
        },
      ];

      for (const item of updates) {
        const res = await savePropertySetting(item);
        if (!res?.ok) {
          setSaving(false);
          message.error(res?.msg || `Failed to save ${item.property}`);
          return;
        }
      }

      setSaving(false);
      message.success("Transport settings updated successfully");
      loadTransportSettings();
    } catch (error) {
      if (error?.errorFields) return;
      setSaving(false);
      message.error("Failed to save transport settings");
    }
  };

  return (
    <div>
      <Row gutter={isLgUp ? 24 : 12}>
        <Col xs={24} lg={14}>
          <SettingsSection
            title="Fuel Policy"
            description="Only backend-supported transport settings are managed here."
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={saveTransportSettings}
              initialValues={{
                couponExpiryDays: 30,
                fuel_litres_budget_ceiling: 200,
              }}
            >
              <Row gutter={isLgUp ? 16 : 12}>
                <Col span={12}>
                  <Form.Item
                    name="couponExpiryDays"
                    label="Coupon Expiry (Days)"
                    rules={[
                      {
                        required: true,
                        message: "Coupon expiry days is required",
                      },
                      {
                        type: "number",
                        min: 1,
                        message: "Value must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      style={{ width: "100%" }}
                      addonAfter="days"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="fuel_litres_budget_ceiling"
                    label="Fuel Request Threshold (L)"
                    rules={[
                      {
                        required: true,
                        message: "Fuel request threshold is required",
                      },
                      {
                        type: "number",
                        min: 1,
                        message: "Value must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      style={{ width: "100%" }}
                      addonAfter="litres"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />
              <Space
                direction={isLgUp ? "horizontal" : "vertical"}
                style={{ width: isLgUp ? "auto" : "100%" }}
              >
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={saving}
                >
                  Save Changes
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadTransportSettings}
                  loading={loading}
                >
                  Reset
                </Button>
              </Space>
            </Form>
          </SettingsSection>
        </Col>
      </Row>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN: System Settings Page with Tabs
// ═══════════════════════════════════════════════════════════════════════════════
const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState("general");
  const screens = useBreakpoint();
  const isLgUp = !!screens.lg;

  const tabItems = [
    {
      key: "general",
      label: (
        <span>
          <BankOutlined className="me-1" />
          General
        </span>
      ),
      children: <GeneralSettings />,
    },
    {
      key: "academics",
      label: (
        <span>
          <BookOutlined className="me-1" />
          Academics
        </span>
      ),
      children: <AcademicSettings />,
    },
    {
      key: "calendar",
      label: (
        <span>
          <CalendarOutlined className="me-1" />
          Academic Calendar
        </span>
      ),
      children: <AcademicCalendarSettings />,
    },
    {
      key: "admissions",
      label: (
        <span>
          <TeamOutlined className="me-1" />
          Admissions
        </span>
      ),
      children: <AdmissionsSettings />,
    },
    {
      key: "transport",
      label: (
        <span>
          <CarOutlined className="me-1" />
          Transport
        </span>
      ),
      children: <TransportSettings />,
    },
  ];

  return (
    <div
      className="container-fluid"
      style={{ padding: isLgUp ? "16px 24px" : "12px 8px" }}
    >
      <Card
        bordered={false}
        className="criclebox tablespace border-0 mb-24"
        style={{ padding: isLgUp ? "24px 32px" : "12px 12px" }}
      >
        <div className="mb-4" style={{ paddingBottom: 8 }}>
          <Title level={3} className="mb-1">
            System Settings
          </Title>
          <Text type="secondary">
            Configure and manage all system-wide settings for Hillside Schools.
          </Text>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabPosition={isLgUp ? "left" : "top"}
          size="large"
          style={{ minHeight: 600 }}
          tabBarStyle={
            isLgUp ? { width: 200, paddingRight: 16 } : { width: "100%" }
          }
          tabBarGutter={4}
        />
      </Card>
    </div>
  );
};

export default SystemSettings;
